import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import * as PoliceableActions from 'collaborator-components/PoliceableActions';
import {StringIDFolder} from 'constants/FolderConstants';
import {RegistrySummary} from 'constants/RegistryConstants';
import ExportModal from 'exports/ExportModal';
import {FolderSettingsModalWrapper as FolderSettingsModal} from 'file-browser/FolderSettingsModal';
import {ProjectSettingsModal, ProjectSettingsModalProps} from 'file-browser/ProjectSettingsModal';
import * as SourceActions from 'file-browser/SourceActions';
import * as RegistryUtil from 'registry/RegistryUtil';
import EntityStore from 'stores/EntityStore';
import legacyRunModal from 'ui-library/legacyRunModal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {ElementMouseEvent} from 'util/Types';

import SettingsIconDropdown from '../SettingsIconDropdown';

interface SettingsIconProps {
  // TODO (Steven BNCH-10870): Add stricter typing to make permissions required
  folder: StringIDFolder;
  onNavigateToRoot: () => void;
}

class FolderSettingsIcon extends React.Component<SettingsIconProps> {
  _handleOpenExportModal = (): void => {
    ExportModal.openForFolder(this.props.folder);
  };

  _getRegistrySettingsURL = (): string => {
    const source = this.props.folder.source as RegistrySummary;
    return RegistryUtil.getRegistrySettingsUrl(source.s_owner.url_identifier, source.id);
  };

  _handleOpenProjectSettings = (): void => {
    assert(this.props.folder.permissions != null, 'Expected project to have permissions');
    legacyRunModal<ProjectSettingsModalProps>(ProjectSettingsModal, {
      permissions: this.props.folder.permissions,
      sourceId: this.props.folder.source!.id,
      onDelete: this.props.onNavigateToRoot,
    });
  };
  _handleOpenFolderSettings = (): void => {
    legacyRunModal(FolderSettingsModal, {
      folderId: this.props.folder.id,
      isReadOnly: !(this.props.folder.permissions?.writable ?? true),
    });
  };

  _handleOpenCollaborationManangement = (): void => {
    const folder = this.props.folder;
    if (folder.parent == null) {
      SourceActions.openSavingCollaborationManagementModal(folder.source!);
    } else {
      PoliceableActions.openSavingCollaborationManagementModal(folder.id);
    }
  };

  _handleExportAuditLogs = (): void => {
    const id = this.props.folder.parent != null ? this.props.folder.id : this.props.folder.source!.id;
    AuditActions.exportAuditLogs([id]);
  };

  _handleCopyLink = (): void => {
    const folderURL = BenchlingUtil.pathToURL(this.props.folder.url);
    copyToClipboard(folderURL, {showToastOnSuccess: true});
  };

  override render(): JSX.Element | null {
    const isSignedIn = EntityStore.currentUser() != null;
    if (!isSignedIn) {
      return null;
    }
    let options = [];
    if (RegistryUtil.isRegistryFolder(this.props.folder)) {
      options.push({
        name: 'Registry settings',
        onSelect: (e: ElementMouseEvent) => {
          BenchlingUtil.navigate(e, this._getRegistrySettingsURL());
        },
        href: this._getRegistrySettingsURL(),
        dataTestAttrs: {component: 'FolderSettingsIcon', element: 'registrySettings'},
      });
      if (BenchlingConfig.ENABLE_EXPORTS) {
        options.push({
          name: 'Export registry',
          onSelect: this._handleOpenExportModal,
        });
      }
    } else if (this.props.folder.parent) {
      options.push({name: 'Folder settings', onSelect: this._handleOpenFolderSettings});
      if (BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS) {
        options.push({
          name: 'Manage access',
          onSelect: this._handleOpenCollaborationManangement,
        });
      }
      if (BenchlingConfig.ENABLE_EXPORTS) {
        options.push({
          name: 'Export folder',
          onSelect: this._handleOpenExportModal,
        });
      }
      options.push({
        name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
        onSelect: this._handleExportAuditLogs,
        dataTestAttrs: {component: 'FolderSettingsIcon', element: 'exportAuditLogOption'},
      });
      options.push({
        name: 'Copy folder link',
        onSelect: this._handleCopyLink,
      });
    } else {
      options = _.compact([
        {
          name: 'Project settings',
          onSelect: this._handleOpenProjectSettings,
          dataTestAttrs: {component: 'FolderSettingsIcon', element: 'projectSettingOption'},
        },
        {
          name: 'Manage access',
          onSelect: this._handleOpenCollaborationManangement,
        },
        BenchlingConfig.ENABLE_EXPORTS && {
          name: 'Export project',
          onSelect: this._handleOpenExportModal,
        },
        {
          name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
          onSelect: this._handleExportAuditLogs,
          dataTestAttrs: {component: 'FolderSettingsIcon', element: 'exportAuditLogOption'},
        },
        {
          name: 'Copy project link',
          onSelect: this._handleCopyLink,
        },
      ]);
    }
    return <SettingsIconDropdown options={options} />;
  }
}

export default FolderSettingsIcon;
